import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useOwnerScope } from '../../../src/context/OwnerScopeContext';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import Input from '../../../src/components/Input';
import UpsellPolicyPicker, { type UpsellPolicy } from '../../../src/components/upsells/UpsellPolicyPicker';
import Picker from '../../../src/components/Picker';
import { useToast } from '../../../src/components/Toast';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';

interface Location {
  id: string;
  name: string;
}

interface WashTier {
  id: string;
  name: string;
  priceInCents: number;
  isActive: boolean;
}

/** AddPlanScreen - create a new subscription membership plan. */
export default function AddPlanScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [washTiers, setWashTiers] = useState<WashTier[]>([]);
  const [stripeOnboarded, setStripeOnboarded] = useState<boolean | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [planType, setPlanType] = useState<'automatic' | 'self_serve' | 'mixed'>('automatic');
  const [tierId, setTierId] = useState('');
  const [upsellCatalog, setUpsellCatalog] = useState<{ id: string; name: string; priceCents: number; isActive: boolean }[]>([]);
  const [upsellPolicy, setUpsellPolicy] = useState<UpsellPolicy>({}); // 1.4 R05: per upsell none | purchasable | included
  const [priceMonthly, setPriceMonthly] = useState('');
  const [maxWashesPerDay, setMaxWashesPerDay] = useState('');
  const [maxWashesPerMonth, setMaxWashesPerMonth] = useState('');
  const [maxMinutesPerDay, setMaxMinutesPerDay] = useState('');
  const [maxMinutesPerMonth, setMaxMinutesPerMonth] = useState('');
  // Optional vacuum-minute benefit — off sends null for both caps.
  const [includeVacMinutes, setIncludeVacMinutes] = useState(false);
  const [maxVacMinutesPerDay, setMaxVacMinutesPerDay] = useState('');
  const [maxVacMinutesPerMonth, setMaxVacMinutesPerMonth] = useState('');
  // Optional vending-credit benefit — dollars/month, no unlimited mode.
  const [includeVendingCredit, setIncludeVendingCredit] = useState(false);
  const [vendingCreditMonthly, setVendingCreditMonthly] = useState('');
  // Membership sessions ignore the bay's per-session minimum when on —
  // stopping early deducts only the actual elapsed minutes.
  const [overrideMinimums, setOverrideMinimums] = useState(false);
  const [locationScope, setLocationScope] = useState('all');
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);

  const includesAutomatic = planType === 'automatic' || planType === 'mixed';
  const includesSelfServe = planType === 'self_serve' || planType === 'mixed';

  const router = useRouter();
  const toast = useToast();
  const scope = useOwnerScope();

  useEffect(() => {
    const ac = new AbortController();
    fetchData(ac.signal);
    return () => ac.abort();
  }, []);

  const fetchData = async (signal?: AbortSignal) => {
    try {
      // Check Stripe onboarding status
      try {
        const status = await scope.api.get<{ onboarded: boolean }>('/api/stripe/connect/status');
        if (!signal?.aborted) setStripeOnboarded(status.onboarded);
      } catch { if (!signal?.aborted) setStripeOnboarded(false); }

      const locs = await scope.api.get<Location[]>('/api/locations');
      if (signal?.aborted) return;
      setLocations(locs);

      // Wash tiers come from the owner's catalog (source of truth).
      const catalog = await scope.api.get<WashTier[]>('/api/wash-tiers');
      // 1.4 P14: upsells members get free. Optional on older servers.
      scope.api.get<{ id: string; name: string; priceCents: number; isActive: boolean }[]>('/api/upsells')
        .then(list => setUpsellCatalog(list.filter(u => u.isActive))).catch(() => setUpsellCatalog([]));
      if (signal?.aborted) return;
      const activeTiers = catalog.filter(t => t.isActive);
      setWashTiers(activeTiers);
      if (activeTiers.length > 0) setTierId(activeTiers[0].id);
    } catch (err: any) {
      if (!(signal?.aborted)) toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Plan name is required' });
      return;
    }
    if (includesAutomatic) {
      if (!tierId) { toast.show({ type: 'error', title: 'Error', message: 'Select a wash tier' }); return; }
      // 0 is valid: it means unlimited.
      if (isNaN(parseInt(maxWashesPerDay))) { toast.show({ type: 'error', title: 'Error', message: 'Enter max washes per day (0 = unlimited)' }); return; }
      if (isNaN(parseInt(maxWashesPerMonth))) { toast.show({ type: 'error', title: 'Error', message: 'Enter max washes per month (0 = unlimited)' }); return; }
      if (parseInt(maxWashesPerDay) > 0 && parseInt(maxWashesPerMonth) > 0 && parseInt(maxWashesPerDay) > parseInt(maxWashesPerMonth)) { toast.show({ type: 'error', title: 'Error', message: 'Daily wash limit cannot exceed the monthly limit' }); return; }
    }
    if (includesSelfServe) {
      if (isNaN(parseInt(maxMinutesPerDay))) { toast.show({ type: 'error', title: 'Error', message: 'Enter max self-serve minutes per day (0 = unlimited)' }); return; }
      if (isNaN(parseInt(maxMinutesPerMonth))) { toast.show({ type: 'error', title: 'Error', message: 'Enter max self-serve minutes per month (0 = unlimited)' }); return; }
      if (parseInt(maxMinutesPerDay) > 0 && parseInt(maxMinutesPerMonth) > 0 && parseInt(maxMinutesPerDay) > parseInt(maxMinutesPerMonth)) { toast.show({ type: 'error', title: 'Error', message: 'Daily minute limit cannot exceed the monthly limit' }); return; }
    }
    if (includeVacMinutes) {
      if (isNaN(parseInt(maxVacMinutesPerDay))) { toast.show({ type: 'error', title: 'Error', message: 'Enter max vacuum minutes per day (0 = unlimited)' }); return; }
      if (isNaN(parseInt(maxVacMinutesPerMonth))) { toast.show({ type: 'error', title: 'Error', message: 'Enter max vacuum minutes per month (0 = unlimited)' }); return; }
      if (parseInt(maxVacMinutesPerDay) > 0 && parseInt(maxVacMinutesPerMonth) > 0 && parseInt(maxVacMinutesPerDay) > parseInt(maxVacMinutesPerMonth)) { toast.show({ type: 'error', title: 'Error', message: 'Daily vacuum minute limit cannot exceed the monthly limit' }); return; }
    }
    const vendingCreditCents = includeVendingCredit ? Math.round(parseFloat(vendingCreditMonthly) * 100) : null;
    if (includeVendingCredit && (!vendingCreditCents || vendingCreditCents <= 0)) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a valid monthly vending credit amount' });
      return;
    }
    const priceCents = Math.round(parseFloat(priceMonthly) * 100);
    if (!priceCents || priceCents <= 0) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a valid monthly price' });
      return;
    }
    if (locationScope === 'selected' && selectedLocationIds.length === 0) {
      toast.show({ type: 'error', title: 'Error', message: 'Select at least one location' });
      return;
    }

    try {
      setSubmitting(true);
      await scope.api.post('/api/subscription-plans', {
        name: name.trim(),
        description: description.trim() || undefined,
        planType,
        tierId: includesAutomatic ? tierId : undefined,
        upsellPolicy,
        includedUpsellIds: Object.entries(upsellPolicy).filter(([, v]) => v === 'included').map(([k]) => k),
        priceMonthly: priceCents,
        maxWashesPerDay: includesAutomatic ? parseInt(maxWashesPerDay) : undefined,
        maxWashesPerMonth: includesAutomatic ? parseInt(maxWashesPerMonth) : undefined,
        maxMinutesPerDay: includesSelfServe ? parseInt(maxMinutesPerDay) : undefined,
        maxMinutesPerMonth: includesSelfServe ? parseInt(maxMinutesPerMonth) : undefined,
        maxVacMinutesPerDay: includeVacMinutes ? parseInt(maxVacMinutesPerDay) : null,
        maxVacMinutesPerMonth: includeVacMinutes ? parseInt(maxVacMinutesPerMonth) : null,
        vendingCreditCentsPerMonth: vendingCreditCents,
        overrideMinimums,
        locationScope,
        locationIds: locationScope === 'selected' ? selectedLocationIds : undefined,
      });
      toast.show({ type: 'success', title: 'Plan Created' });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLocationId = (id: string) => {
    setSelectedLocationIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const tierOptions = washTiers.map(t => ({ label: `${t.name} ($${(t.priceInCents / 100).toFixed(2)})`, value: t.id }));

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
      {stripeOnboarded === false && (
        <Card style={[styles.formCard, { borderColor: colors.warning, borderWidth: 1 }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
            <Ionicons name="warning" size={20} color={colors.warning} />
            <View style={{ flex: 1 }}>
              <Text style={[styles.sectionTitle, { marginBottom: 4, color: colors.warning }]}>Stripe Not Connected</Text>
              <Text style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>
                Set up Stripe in Settings before creating plans.
              </Text>
            </View>
          </View>
        </Card>
      )}
      <Card style={styles.formCard}>
        <Text style={styles.sectionTitle}>Plan Details</Text>

        <Input label="Plan Name" value={name} onChangeText={setName} placeholder="e.g. Basic Monthly" />
        <Input label="Description (optional)" value={description} onChangeText={setDescription} placeholder="Describe the plan benefits" multiline maxLength={500} />

        <Text style={styles.label}>Plan Type</Text>
        <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md, flexWrap: 'wrap' }}>
          {(['automatic', 'self_serve', 'mixed'] as const).map(pt => (
            <TouchableOpacity
              key={pt}
              onPress={() => setPlanType(pt)}
              style={[styles.scopeChip, planType === pt && { backgroundColor: colors.primary, borderColor: colors.primary }]}
            >
              <Text style={[styles.scopeChipText, planType === pt && { color: '#fff' }]}>
                {pt === 'automatic' ? 'Automatic only' : pt === 'self_serve' ? 'Self-serve only' : 'Mixed'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {includesAutomatic && (
          tierOptions.length > 0 ? (
            <>
            <Picker label="Wash Tier" options={tierOptions} value={tierId} onValueChange={setTierId} />
            <UpsellPolicyPicker catalog={upsellCatalog} value={upsellPolicy} onChange={setUpsellPolicy} title="Add-ons for members" />
            </>
          ) : (
            <View style={{ marginBottom: spacing.md }}>
              <Text style={styles.label}>Wash Tier</Text>
              <Text style={styles.hintText}>No wash tiers yet. Create them under "Manage Wash Tiers" first.</Text>
            </View>
          )
        )}

        <Input
          label="Monthly Price ($)"
          value={priceMonthly}
          onChangeText={setPriceMonthly}
          placeholder=""
          keyboardType="decimal-pad"
        />
      </Card>

      {includesAutomatic && (
        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Automatic Wash Limits</Text>
          <Text style={styles.unlimitedHint}>Set a limit to 0 for unlimited washes.</Text>

          <Input
            label="Max Washes Per Day"
            value={maxWashesPerDay}
            onChangeText={setMaxWashesPerDay}
            placeholder=""
            keyboardType="number-pad"
          />

          <Input
            label="Max Washes Per Month"
            value={maxWashesPerMonth}
            onChangeText={setMaxWashesPerMonth}
            placeholder=""
            keyboardType="number-pad"
          />
        </Card>
      )}

      {includesSelfServe && (
        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Self-Serve Minute Limits</Text>
          <Text style={styles.unlimitedHint}>Set a limit to 0 for unlimited minutes.</Text>

          <Input
            label="Max Minutes Per Day"
            value={maxMinutesPerDay}
            onChangeText={setMaxMinutesPerDay}
            placeholder=""
            keyboardType="number-pad"
          />

          <Input
            label="Max Minutes Per Month"
            value={maxMinutesPerMonth}
            onChangeText={setMaxMinutesPerMonth}
            placeholder=""
            keyboardType="number-pad"
          />
        </Card>
      )}

      <Card style={styles.formCard}>
        <Text style={styles.sectionTitle}>Vacuum Minutes</Text>
        <TouchableOpacity style={styles.checkRow} onPress={() => setIncludeVacMinutes(!includeVacMinutes)}>
          <Ionicons name={includeVacMinutes ? 'checkbox' : 'square-outline'} size={24} color={includeVacMinutes ? colors.primary : colors.textLight} />
          <Text style={styles.checkLabel}>Include vacuum minutes</Text>
        </TouchableOpacity>
        {includeVacMinutes && (
          <>
            <Text style={styles.unlimitedHint}>Set a limit to 0 for unlimited minutes.</Text>
            <Input
              label="Max Vacuum Minutes Per Day"
              value={maxVacMinutesPerDay}
              onChangeText={(v: string) => setMaxVacMinutesPerDay(v.replace(/[^0-9]/g, ''))}
              placeholder=""
              keyboardType="number-pad"
            />
            <Input
              label="Max Vacuum Minutes Per Month"
              value={maxVacMinutesPerMonth}
              onChangeText={(v: string) => setMaxVacMinutesPerMonth(v.replace(/[^0-9]/g, ''))}
              placeholder=""
              keyboardType="number-pad"
            />
          </>
        )}
      </Card>

      {/* Minute-based benefits carry the bay's per-session minimum by
          default; this override lets stop-early sessions deduct only the
          time actually used. */}
      {(includesSelfServe || includeVacMinutes) && (
        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Session Minimums</Text>
          <TouchableOpacity style={styles.checkRow} onPress={() => setOverrideMinimums(!overrideMinimums)}>
            <Ionicons name={overrideMinimums ? 'checkbox' : 'square-outline'} size={24} color={overrideMinimums ? colors.primary : colors.textLight} />
            <Text style={[styles.checkLabel, { flex: 1 }]}>Override minimums — membership sessions ignore the bay minimum</Text>
          </TouchableOpacity>
          <Text style={styles.hintText}>
            Off: stopping a membership session early still deducts the bay's minimum minutes.
          </Text>
        </Card>
      )}

      <Card style={styles.formCard}>
        <Text style={styles.sectionTitle}>Vending Credit</Text>
        <TouchableOpacity style={styles.checkRow} onPress={() => setIncludeVendingCredit(!includeVendingCredit)}>
          <Ionicons name={includeVendingCredit ? 'checkbox' : 'square-outline'} size={24} color={includeVendingCredit ? colors.primary : colors.textLight} />
          <Text style={styles.checkLabel}>Include vending credit</Text>
        </TouchableOpacity>
        {includeVendingCredit && (
          <Input
            label="Vending Credit Per Month ($)"
            value={vendingCreditMonthly}
            onChangeText={(v: string) => setVendingCreditMonthly(v.replace(/[^0-9.]/g, ''))}
            placeholder="5.00"
            keyboardType="decimal-pad"
          />
        )}
      </Card>

      <Card style={styles.formCard}>
        <Text style={styles.sectionTitle}>Location Scope</Text>
        <Text style={styles.label}>Where can members use this plan?</Text>
        <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm }}>
          {(['all', 'selected'] as const).map(scope => (
            <TouchableOpacity
              key={scope}
              onPress={() => setLocationScope(scope)}
              style={[styles.scopeChip, locationScope === scope && { backgroundColor: colors.primary, borderColor: colors.primary }]}
            >
              <Text style={[styles.scopeChipText, locationScope === scope && { color: '#fff' }]}>
                {scope === 'all' ? 'All Locations' : 'Selected'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {locationScope === 'selected' && locations.length > 0 && (
          <View style={{ gap: spacing.xs }}>
            {locations.map(loc => (
              <TouchableOpacity key={loc.id} onPress={() => toggleLocationId(loc.id)} style={[styles.locOption, selectedLocationIds.includes(loc.id) && { borderColor: colors.primary, backgroundColor: colors.primary + '10' }]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                  <Ionicons name={selectedLocationIds.includes(loc.id) ? 'checkbox' : 'square-outline'} size={20} color={selectedLocationIds.includes(loc.id) ? colors.primary : colors.textLight} />
                  <Text style={[styles.locOptionText, selectedLocationIds.includes(loc.id) && { color: colors.primary, fontWeight: fontWeight.semibold }]}>{loc.name}</Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </Card>

      <Button
        title="Create Plan"
        onPress={handleSubmit}
        loading={submitting}
        disabled={stripeOnboarded === false}
        size="lg"
        style={{ marginTop: spacing.md }}
      />

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  formCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  unlimitedHint: { fontSize: fontSize.xs, color: c.textLight, marginBottom: spacing.sm },
  label: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, marginBottom: spacing.xs },
  hintText: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic' },
  scopeChip: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  scopeChipText: { fontSize: fontSize.sm, color: c.text },
  locOption: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  locOptionText: { fontSize: fontSize.sm, color: c.text },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, marginBottom: spacing.xs },
  checkLabel: { fontSize: fontSize.sm, color: c.text },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
